import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useListMyTicketsQuery } from '../../features/tickets/ticketsApi';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import TicketCard from '../../components/tickets/TicketCard';
import EmptyState from '../../components/ui/EmptyState';
import { MessageCircleIcon } from '../../components/ui/icons';
import { SkeletonCardGrid } from '../../components/ui/Skeleton';
import { authErrorMessage } from '../../lib/authErrorMessage';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

/**
 * GET /investor/support-tickets — scoped automatically to the logged-in
 * investor, no permission check beyond authentication. Real support-ticket
 * endpoints replace the old ComingSoon stub (there was genuinely no
 * backend for this before v1.14.0).
 */
export default function Tickets() {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useListMyTicketsQuery({ status: status || undefined, page });

  const tickets = data?.tickets ?? [];
  const meta = data?.meta;
  const parseError = data?.parseError;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold text-foreground">Support</h1>
          <Badge tone="success">Live</Badge>
        </div>
        <Link to="/tickets/new">
          <Button>New ticket</Button>
        </Link>
      </div>

      <Card>
        <CardHeader
          title="My tickets"
          action={
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className="rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          }
        />

        {isLoading && <SkeletonCardGrid count={6} />}

        {isError && (
          <p className="py-8 text-center text-danger">{authErrorMessage(error, { fallback: 'Could not load your tickets.' })}</p>
        )}

        {!isLoading && !isError && parseError && (
          <p className="py-8 text-center text-danger">Something went wrong loading this data. Check the console for details.</p>
        )}

        {!isLoading && !isError && !parseError && tickets.length === 0 && (
          <EmptyState
            icon={MessageCircleIcon}
            title="No support tickets yet"
            body="Open one if you need help — we'll get back to you here."
            action={
              <Link to="/tickets/new">
                <Button size="sm">New ticket</Button>
              </Link>
            }
          />
        )}

        {!isLoading && !isError && !parseError && tickets.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tickets.map((t) => (
                <TicketCard key={t.id} ticket={t} onClick={() => navigate(`/tickets/${t.id}`)} />
              ))}
            </div>

            {meta && meta.lastPage > 1 && (
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
                <p className="text-xs text-foreground-subtle">
                  Page {meta.currentPage} of {meta.lastPage} · {meta.total} total
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" disabled={meta.currentPage <= 1} onClick={() => setPage(Math.max(1, meta.currentPage - 1))}>
                    Previous
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={meta.currentPage >= meta.lastPage}
                    onClick={() => setPage(Math.min(meta.lastPage, meta.currentPage + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
